import { CanceledError } from 'axios';

type CachedRead = {
    expiresAt: number;
    value: unknown;
};

const inFlightReads = new Map<string, Promise<unknown>>();
const cachedReads = new Map<string, CachedRead>();
// Reads started ahead of their consumer (for example while a route is still
// loading). The first consumer takes the result; nobody else ever sees it.
const primedReads = new Map<string, { expiresAt: number; value: Promise<unknown> }>();
let resetGeneration = 0;

const takePrimed = (key: string): Promise<unknown> | undefined => {
    const primed = primedReads.get(key);
    primedReads.delete(key);
    return primed && primed.expiresAt > Date.now() ? primed.value : undefined;
};

export type SharedReadOptions = {
    cacheMs?: number;
    force?: boolean;
};

export const readSharedResource = <T>(
    key: string,
    loader: () => Promise<T>,
    options: SharedReadOptions = {},
): Promise<T> => {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey) {
        return loader();
    }

    const now = Date.now();
    const force = Boolean(options.force);
    const cacheMs = Math.max(0, Math.floor(options.cacheMs ?? 0));

    if (!force) {
        const cached = cachedReads.get(normalizedKey);
        if (cached && cached.expiresAt > now) {
            return Promise.resolve(cached.value as T);
        }

        const pending = inFlightReads.get(normalizedKey);
        if (pending) {
            primedReads.delete(normalizedKey);
            return pending as Promise<T>;
        }
        const primed = takePrimed(normalizedKey);
        if (primed) {
            return primed as Promise<T>;
        }
    }

    const generation = resetGeneration;
    const request: Promise<T> = loader()
        .then((value): T | Promise<T> => {
            if (generation !== resetGeneration) throw new CanceledError('Read scope was cleared');
            // Existing awaiters also need current state after invalidation.
            // Reuse the newer request/cache, or fetch once if it finished.
            if (inFlightReads.get(normalizedKey) !== request) {
                return readSharedResource(normalizedKey, loader, { cacheMs });
            }
            if (cacheMs > 0 && inFlightReads.get(normalizedKey) === request) {
                cachedReads.set(normalizedKey, {
                    value,
                    expiresAt: Date.now() + cacheMs,
                });
            }
            return value;
        }, (error) => {
            if (generation !== resetGeneration) throw new CanceledError('Read scope was cleared');
            if (inFlightReads.get(normalizedKey) !== request) {
                return readSharedResource(normalizedKey, loader, { cacheMs });
            }
            throw error;
        })
        .finally(() => {
            if (inFlightReads.get(normalizedKey) === request) {
                inFlightReads.delete(normalizedKey);
            }
        });

    inFlightReads.set(normalizedKey, request);
    return request;
};

/**
 * Start a read before its consumer exists. The consumer's next readSharedResource
 * (or takePrimedResource) call with the same key receives this result once, if it
 * arrives within ttlMs; later reads go to the network as usual.
 */
export const primeSharedResource = <T>(key: string, loader: () => Promise<T>, ttlMs = 15_000): Promise<T> => {
    const normalizedKey = String(key || '').trim();
    // While in flight the read is shared like any other; once it has finished,
    // the first consumer that asks for it takes the result.
    const value = readSharedResource(normalizedKey, loader);
    // Unconsumed failures stay silent; the consumer sees them when it takes the read.
    value.catch(() => undefined);
    if (normalizedKey) primedReads.set(normalizedKey, { expiresAt: Date.now() + ttlMs, value });
    return value;
};

export const takePrimedResource = <T>(key: string): Promise<T> | undefined => (
    takePrimed(String(key || '').trim()) as Promise<T> | undefined
);

export const invalidateSharedResource = (keyOrPrefix: string): void => {
    const normalized = String(keyOrPrefix || '').trim();
    if (!normalized) {
        return;
    }

    for (const key of Array.from(cachedReads.keys())) {
        if (key === normalized || key.startsWith(normalized)) {
            cachedReads.delete(key);
        }
    }
    // A read that started before a successful mutation must not repopulate the
    // cache or be shared with a consumer requesting the new state.
    for (const key of Array.from(inFlightReads.keys())) {
        if (key === normalized || key.startsWith(normalized)) {
            inFlightReads.delete(key);
        }
    }
    for (const key of Array.from(primedReads.keys())) {
        if (key === normalized || key.startsWith(normalized)) {
            primedReads.delete(key);
        }
    }
};

export const clearSharedReadResources = (): void => {
    resetGeneration += 1;
    inFlightReads.clear();
    cachedReads.clear();
    primedReads.clear();
};

export const clearSharedReadResourcesForTests = clearSharedReadResources;
